import React, { useEffect, useState } from 'react';
import Logo from '../../assets/logo-sm.svg';
import { Link } from 'react-router-dom';
import { confirmMfaReset } from '../../api/services/authService';

type Status = 'loading' | 'success' | 'error';

const ConfirmMfaReset = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const token = queryParams.get('token') ?? '';

  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No reset token was provided. Please request a new MFA reset link.');
      return;
    }

    confirmMfaReset(token)
      .then(() => {
        setStatus('success');
      })
      .catch((err: any) => {
        setStatus('error');
        setErrorMessage(
          err?.response?.data?.message
          ?? err?.message
          ?? 'This MFA reset link is invalid or has expired. Please request a new one.'
        );
      });
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className='flex bg-ptsblue-normal flex-col w-full items-center justify-center'
      style={{ height: '100vh' }}
    >
      <div className='text-gray-900 w-full p-6 md:max-w-xl'>
        <div className='bg-white rounded-lg border shadow-lg p-10 mb-2'>
          <img
            src={Logo}
            alt='Pediatric Therapeutic Services'
            className='object-contain object-center h-28 w-full mb-6'
          />

          {status === 'loading' && (
            <div className='text-center'>
              <p className='text-gray-600 text-sm'>Verifying your reset link…</p>
            </div>
          )}

          {status === 'success' && (
            <div className='text-center'>
              <div className='mb-4 flex justify-center'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-16 w-16 text-green-500'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <h2 className='text-xl font-semibold mb-2'>MFA Successfully Reset</h2>
              <p className='text-sm text-gray-600 mb-6'>
                Your two-factor authentication has been cleared. The next time you
                log in, you will be guided through setting up a new authenticator app.
              </p>
              <Link
                to='/login'
                className='inline-block px-6 py-3 bg-ptsgreen-normal text-white rounded font-semibold text-sm hover:bg-ptshover hover:text-black'
              >
                Go to Login
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className='text-center'>
              <div className='mb-4 flex justify-center'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-16 w-16 text-red-500'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <h2 className='text-xl font-semibold mb-2'>Reset Link Invalid</h2>
              <p className='text-sm text-gray-600 mb-6'>{errorMessage}</p>
              <Link
                to='/request-mfa-reset'
                className='inline-block px-6 py-3 bg-ptsblue-normal text-white rounded font-semibold text-sm hover:bg-ptshover hover:text-black'
              >
                Request a New Reset Link
              </Link>
            </div>
          )}
        </div>

        <p className='text-right text-gray-300 text-xs'>
          &copy;Copyright 2026 – Kelly Pediatric Therapy.
          <br />
          All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ConfirmMfaReset;
